import { Navigate, Route, Routes } from "react-router-dom";

import { AdminOnly, AppShell } from "./components/AppShell";
import { AuthProvider, hasAccess, useAuth } from "./lib/auth-context";
import { useViewAsUser } from "./lib/view-as";
import { Loading } from "./screens/Loading";
import { Login } from "./screens/Login";
import { Calendario } from "./screens/Calendario";
import { Claude } from "./screens/Claude";
import { MisVuelos } from "./screens/MisVuelos";
import { NoAccess } from "./screens/NoAccess";
import { Solicitar } from "./screens/Solicitar";
import { SolicitarGracias } from "./screens/SolicitarGracias";
import { AdminAeropuertos } from "./screens/admin/AdminAeropuertos";
import { AdminAviones } from "./screens/admin/AdminAviones";
import { AdminCuentas } from "./screens/admin/AdminCuentas";
import { AdminDistancias } from "./screens/admin/AdminDistancias";
import { AdminFlightDetail } from "./screens/admin/AdminFlightDetail";
import { AdminNotificaciones } from "./screens/admin/AdminNotificaciones";
import { AdminPasajeros } from "./screens/admin/AdminPasajeros";
import { AdminResumen } from "./screens/admin/AdminResumen";
import { AdminUsuarios } from "./screens/admin/AdminUsuarios";
import { AdminVuelos } from "./screens/admin/AdminVuelos";

/**
 * Top-level shell. Decides:
 *   - <Loading /> while auth + Usuarios doc resolve
 *   - <Login /> when signed out
 *   - <NoAccess /> when signed in but the Usuarios doc has no roles
 *   - <AppShell /> with routes otherwise
 *
 * Bare "/" smart-redirects: admin -> /admin, everyone else -> /mis-vuelos.
 */
export function App() {
  return (
    <AuthProvider>
      <Gate />
    </AuthProvider>
  );
}

function Gate() {
  const { firebaseUser, usuario, loading } = useAuth();
  const [viewAsUser] = useViewAsUser();
  if (loading) return <Loading />;
  if (!firebaseUser) return <Login />;
  if (!hasAccess(usuario)) return <NoAccess />;

  // Effective admin: real admin AND not currently in "Vista usuario".
  // The / redirect uses this so the back-link from any user-facing
  // page lands them back on /mis-vuelos when in vista usuario.
  const isAdmin = (usuario!.isAdmin || usuario!.isSuperAdmin) && !viewAsUser;

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route
          path="/"
          element={<Navigate to={isAdmin ? "/admin" : "/mis-vuelos"} replace />}
        />
        <Route path="/mis-vuelos" element={<MisVuelos />} />
        <Route path="/solicitar" element={<Solicitar />} />
        <Route path="/solicitar/gracias" element={<SolicitarGracias />} />

        <Route path="/admin" element={<AdminOnly><AdminResumen /></AdminOnly>} />
        <Route path="/admin/flights" element={<AdminOnly><AdminVuelos /></AdminOnly>} />
        {/* Flight detail is reachable from /mis-vuelos too, so it
            renders for any authenticated user. The page itself hides
            admin-only controls (Editar / Aprobar / Cancelar) when the
            viewer isn't admin. */}
        <Route path="/admin/flights/:id" element={<AdminFlightDetail />} />
        <Route path="/admin/users" element={<AdminOnly><AdminUsuarios /></AdminOnly>} />
        <Route path="/admin/passengers" element={<AdminPasajeros />} />
        <Route path="/admin/accounts" element={<AdminOnly><AdminCuentas /></AdminOnly>} />
        <Route path="/admin/aircraft" element={<AdminOnly><AdminAviones /></AdminOnly>} />
        <Route path="/admin/locations" element={<AdminOnly><AdminAeropuertos /></AdminOnly>} />
        <Route path="/admin/distances" element={<AdminOnly><AdminDistancias /></AdminOnly>} />
        <Route path="/admin/notifications" element={<AdminOnly><AdminNotificaciones /></AdminOnly>} />
        {/* Claude Q&A — open to any authorized user (shared fleet visibility).
            The prompt-config panel inside is admin-only. */}
        <Route path="/claude" element={<Claude />} />
        {/* Shared page — admins + users both see it in the top nav. */}
        <Route path="/admin/calendar" element={<Calendario />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}
